import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { EditContextProvider, Form, required, useNotify } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { TextField } from 'shared/mui/Fields';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { TooltipWithInfoIcon } from 'shared/mui/Tooltip';
import { TextField as AdminTextField } from 'shared/react-admin/Fields';
import { NumberInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { Mapping } from 'types/embedded/gatewayRule';

import { GatewayRuleMappingPercentEditDialogSkeleton } from './GatewayRuleMappingPercentEditDialogSkeleton';
import { useMappingPercentEditController } from './hooks/useMappingPercentEditController';

type Props = {
  mapping: Mapping;
} & DialogProps;

export const GatewayRuleMappingPercentEditDialog: React.FC<Props> = ({
  mapping,
  onClose,
  open,
}) => {
  const translate = useTypedTranslate();
  const notify = useNotify();

  const { mappingPercentEditController, isReady } = useMappingPercentEditController({
    mapping,
    onSuccess: () => {
      notify(translate('app.gatewayRules.trafficChangeSuccess'), { type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['mapping_items'] });
      queryClient.invalidateQueries({ queryKey: ['gateway_rules'] });
      onClose();
    },
  });

  return (
    <Dialog onClose={onClose} open={open}>
      <EditContextProvider value={mappingPercentEditController}>
        <Form key={isReady ? 'ready' : 'loading'}>
          <Card sx={{ width: '600px' }}>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title={
                <Box alignItems="flex-end" display="flex" gap="4px">
                  {translate('app.gatewayRules.trafficChange')}
                  <TooltipWithInfoIcon
                    sx={{
                      width: '28px',
                      height: '28px',
                      '& .MuiSvgIcon-root': {
                        width: '20px',
                        height: '20px',
                      },
                    }}
                    tooltipText={
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {translate('app.gatewayRules.trafficRule')}
                      </span>
                    }
                  />
                </Box>
              }
              titleSize="smallBold"
            />
            <CardContent>
              <TextField
                disabled
                fullWidth
                label={translate('app.table.method')}
                size="small"
                sx={{ marginBottom: '24px' }}
                value={mapping.method.name}
                variant="outlined"
              />
              <TextInput
                label={translate('app.table.comment')}
                source="notificationText"
                variant="outlined"
              />
              {isReady ? (
                mapping.mappingItems.map((_, index) => {
                  return (
                    <Box display="flex" flexDirection="column" gap={2} key={index}>
                      <ReferenceField
                        link={false}
                        reference="gateways"
                        source={`mappingItems.${index}.gateway.@id`}
                      >
                        <AdminTextField source="name" sx={{ fontSize: '14px', fontWeight: 500 }} />
                      </ReferenceField>
                      <NumberInput
                        InputProps={{
                          inputProps: { min: 0, max: 100 },
                        }}
                        label={translate('app.table.trafficPercentage')}
                        name={`mappingItems.${index}.value`}
                        size="small"
                        source={`mappingItems.${index}.value`}
                        validate={required()}
                        variant="outlined"
                      />
                    </Box>
                  );
                })
              ) : (
                <GatewayRuleMappingPercentEditDialogSkeleton
                  rowsCount={mapping.mappingItems.length}
                />
              )}
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
                <SaveButton
                  disabled={!isReady}
                  key="apply-button"
                  label={translate('app.buttons.apply')}
                />,
              ]}
            />
          </Card>
        </Form>
      </EditContextProvider>
    </Dialog>
  );
};
